import React, { useState } from "react";

export default function ContactForm({ source = "Generel henvendelse", buttonText = "Send besked" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Navn skal udfyldes";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer skal udfyldes";
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Ugyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "E-mail skal udfyldes";
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = "Ugyldig e-mailadresse";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Beskrivelse skal udfyldes";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot
    if (formData.website_url) {
      // Silent fail for bots
      setSubmitStatus("success");
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url,
          source: source,
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (err) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-neutral-200 relative overflow-hidden">
      {/* Subtle background watermark logo */}
      <img 
        src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/1c3d2574-7326-4533-ae86-8c8f28cf4f21/ikon-jg-mesterbyg-removebg-preview.png" 
        alt="" 
        className="absolute -top-10 -right-10 w-72 h-72 opacity-[0.05] pointer-events-none object-contain"
        aria-hidden="true"
      />
      
      <h3 className="text-xl font-bold text-neutral-900 mb-2 flex items-center gap-2">
        <i className="fa-solid fa-file-signature text-yellow-500" />
        Få et uforpligtende tilbud
      </h3>
      <p className="text-sm text-neutral-600 mb-6">
        Udfyld formularen, så vender vi tilbage inden for 24 timer.
      </p>

      {submitStatus === "success" && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl flex items-start gap-3">
          <i className="fa-solid fa-circle-check text-xl text-emerald-500 mt-0.5" />
          <div>
            <h4 className="font-bold">Tak for din henvendelse!</h4>
            <p className="text-sm mt-1">Vi har modtaget dine oplysninger og kontakter dig hurtigst muligt.</p>
          </div>
        </div>
      )}

      {submitStatus === "error" && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation text-xl text-rose-500 mt-0.5" />
          <div>
            <h4 className="font-bold">Der opstod en fejl</h4>
            <p className="text-sm mt-1">Kunne ikke indsende formularen. Prøv venligst igen eller ring til os direkte.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot field - absolutely hidden from humans */}
        <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
          <label htmlFor="website_url">Do not fill this field</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
            Navn <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
              <i className="fa-solid fa-user text-sm" />
            </span>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full pl-10 pr-4 py-2.5 bg-neutral-50 border ${
                errors.name ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
              } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm`}
              placeholder="Dit fulde navn"
            />
          </div>
          {errors.name && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
              Telefon <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                <i className="fa-solid fa-phone text-sm" />
              </span>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2.5 bg-neutral-50 border ${
                  errors.phone ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
                } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm`}
                placeholder="Telefonnummer"
              />
            </div>
            {errors.phone && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
              E-mail <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
                <i className="fa-solid fa-envelope text-sm" />
              </span>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2.5 bg-neutral-50 border ${
                  errors.email ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
                } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm`}
                placeholder="Din e-mailadresse"
              />
            </div>
            {errors.email && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-1">
            Beskrivelse af opgaven <span className="text-rose-500">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            className={`w-full px-4 py-2.5 bg-neutral-50 border ${
              errors.description ? "border-rose-500 focus:ring-rose-200" : "border-neutral-300 focus:ring-yellow-200"
            } rounded-xl text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-4 focus:border-yellow-500 transition-all text-sm resize-none`}
            placeholder="Beskriv dit projekt (f.eks. omfang, materialer, tidsramme)..."
          />
          {errors.description && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.description}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-6 bg-yellow-500 hover:bg-yellow-600 text-neutral-950 font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner animate-spin" />
              Indsender...
            </>
          ) : (
            <>
              <span>{buttonText}</span>
              <i className="fa-solid fa-arrow-right" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
